// Notifications tray — popover with unread notifications list + mark-as-read.
//
// Until the real notifications API lands, this surfaces a 3-item mock so the
// shell shows real chrome. Local state tracks read/unread; persists only for
// the component's lifetime.
//
// Per .forge/plans/v2-nav-shell.md.

import * as LucideIcons from "lucide-react";
import { useMemo, useState, type ComponentType, type CSSProperties, type RefObject } from "react";
import { usePopover } from "./usePopover";

type IconCmp = ComponentType<{
  size?: number;
  "aria-hidden"?: boolean | "true";
  style?: CSSProperties;
}>;

interface MockNotification {
  id: string;
  title: string;
  body: string;
  received_at: string;
}

const MOCK: MockNotification[] = [
  {
    id: "n-1",
    title: "Pledge from Brian Okafor",
    body: "New $250 pledge to the Building Fund.",
    received_at: "2 min ago",
  },
  {
    id: "n-2",
    title: "Reconciliation ready",
    body: "Wallet payout 2026-05-15 matched 18 donations.",
    received_at: "1 hour ago",
  },
  {
    id: "n-3",
    title: "Welcome message scheduled",
    body: "Will send to 14 new donors tomorrow at 9am.",
    received_at: "Yesterday",
  },
];

export function NotificationsTray() {
  const popover = usePopover();
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const unreadCount = useMemo(
    () => MOCK.filter((n) => !readIds.has(n.id)).length,
    [readIds],
  );
  const badgeLabel = unreadCount >= 10 ? "9+" : String(unreadCount);
  const { Bell, Check } = LucideIcons as unknown as {
    Bell: IconCmp;
    Check: IconCmp;
  };

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button
        ref={popover.anchorRef as RefObject<HTMLButtonElement>}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={popover.open}
        aria-label={`Notifications (${unreadCount} unread)`}
        onClick={popover.toggle}
        style={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 32,
          height: 32,
          border: "none",
          background: "transparent",
          color: "var(--color-text-secondary)",
          borderRadius: "var(--radius-md, 8px)",
          cursor: "pointer",
        }}
      >
        <Bell size={18} aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 4,
              right: 4,
              minWidth: 14,
              height: 14,
              padding: "0 3px",
              fontSize: 9,
              fontWeight: 600,
              lineHeight: "14px",
              textAlign: "center",
              color: "var(--color-text-on-color)",
              background: "var(--color-error)",
              borderRadius: 7,
            }}
          >
            {badgeLabel}
          </span>
        )}
      </button>
      {popover.open && (
        <div
          ref={popover.contentRef as RefObject<HTMLDivElement>}
          role="dialog"
          aria-label="Notifications"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 900,
            width: 340,
            maxHeight: "60vh",
            overflowY: "auto",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: "var(--radius-md, 8px)",
            boxShadow: "var(--shadow-medium)",
          }}
        >
          <header
            style={{
              padding: "12px 14px",
              borderBottom: "1px solid var(--color-border-subtle)",
              fontSize: 13,
              fontWeight: 500,
              color: "var(--color-text)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>Notifications</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: "var(--color-text-muted)", fontWeight: 400 }}>
              {unreadCount} unread
            </span>
          </header>
          <ul role="list" style={{ listStyle: "none", padding: 6, margin: 0 }}>
            {MOCK.map((n) => {
              const isRead = readIds.has(n.id);
              return (
                <li key={n.id} role="listitem" style={{ padding: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      padding: "10px 12px",
                      borderRadius: "var(--radius-sm, 6px)",
                      background: isRead ? "transparent" : "var(--color-surface-hover)",
                      opacity: isRead ? 0.6 : 1,
                    }}
                  >
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text)" }}>{n.title}</div>
                      <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{n.body}</div>
                      <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>{n.received_at}</div>
                    </div>
                    {!isRead && (
                      <button
                        type="button"
                        aria-label={`Mark "${n.title}" as read`}
                        onClick={() =>
                          setReadIds((prev) => {
                            const next = new Set(prev);
                            next.add(n.id);
                            return next;
                          })
                        }
                        style={{
                          alignSelf: "flex-start",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 24,
                          height: 24,
                          border: "none",
                          background: "transparent",
                          color: "var(--color-text-muted)",
                          cursor: "pointer",
                          borderRadius: "var(--radius-sm, 6px)",
                        }}
                      >
                        <Check size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
